// @ts-strict-ignore
import { LazyQueryResult } from "@apollo/client/react";
import { ListFilters } from "@dashboard/components/AppLayout/ListFilters";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import BulkProductUploadModal from "@dashboard/components/BulkProductUploadModal";
import { ButtonGroupWithDropdown } from "@dashboard/components/ButtonGroupWithDropdown";
import { DashboardCard } from "@dashboard/components/Card";
import { FilterElement } from "@dashboard/components/Filter";
import { ListPageLayout } from "@dashboard/components/Layouts";
import LimitReachedAlert from "@dashboard/components/LimitReachedAlert";
import { ProductListColumns } from "@dashboard/config";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { getExtensionItemsForOverviewCreate } from "@dashboard/extensions/getExtensionsItems";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import {
  Exact,
  GridAttributesQuery,
  ProductListQuery,
  RefreshLimitsQuery,
  useAvailableColumnAttributesLazyQuery,
} from "@dashboard/graphql";
import { getPrevLocationState } from "@dashboard/hooks/useBackLinkWithState";
import useLocalStorage from "@dashboard/hooks/useLocalStorage";
import useNavigator from "@dashboard/hooks/useNavigator";
import { sectionNames } from "@dashboard/intl";
import {
  ChannelProps,
  PageListProps,
  RelayToFlat,
  SearchPageProps,
  SortPage,
  TabPageProps,
} from "@dashboard/types";
import { hasLimits, isLimitReached } from "@dashboard/utils/limits";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import { ProductListUrlSortField, productUrl } from "../../urls";
import { ProductListDatagrid } from "../ProductListDatagrid";
import { ProductListTiles } from "../ProductListTiles/ProductListTiles";
import { ProductListView } from "../ProductListView/ProductListView";
import { ProductListViewSwitch } from "../ProductListViewSwitch";

export interface ProductListPageProps
  extends PageListProps<ProductListColumns>,
    SearchPageProps,
    Omit<TabPageProps, "onTabDelete" | "onTabDelete">,
    SortPage<ProductListUrlSortField>,
    ChannelProps {
  activeAttributeSortId: string;
  currencySymbol: string;
  gridAttributesOpts: LazyQueryResult<
    GridAttributesQuery,
    Exact<{
      ids: string | string[];
    }>
  >;
  limits: RefreshLimitsQuery["shop"]["limits"];
  products: RelayToFlat<ProductListQuery["products"]>;
  selectedProductIds: string[];
  hasPresetsChanged: boolean;
  onAdd: () => void;
  onImport?: () => void;
  onExport: () => void;
  onTabUpdate: (tabName: string) => void;
  onTabDelete: (tabIndex: number) => void;
  availableColumnsAttributesOpts: ReturnType<typeof useAvailableColumnAttributesLazyQuery>;
  onProductsDelete: () => void;
  onProductsDuplicate: () => void;
  onSelectProductIds: (ids: number[], clearSelection: () => void) => void;
  clearRowSelection: () => void;
  filterDependency?: FilterElement;
  onBulkUpload?: (file: File, onProgress?: (progress: number) => void) => Promise<void>;
}

export type ProductListViewType = "datagrid" | "tile" | "list";

const DEFAULT_PRODUCT_LIST_VIEW_TYPE: ProductListViewType = "datagrid";

export const ProductListPage = (props: ProductListPageProps) => {
  const {
    currencySymbol,
    defaultSettings,
    gridAttributesOpts,
    limits,
    availableColumnsAttributesOpts,
    initialSearch,
    settings,
    onAdd,
    onImport,
    onExport,
    onSearchChange,
    onUpdateListSettings,
    selectedChannelId,
    activeAttributeSortId,
    onTabChange,
    onTabDelete,
    onTabSave,
    onAll,
    currentTab,
    tabs,
    onTabUpdate,
    hasPresetsChanged,
    selectedProductIds,
    onProductsDelete,
    onProductsDuplicate,
    clearRowSelection,
    filterDependency,
    onBulkUpload,
    ...listProps
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigator();
  const [isBulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const limitReached = isLimitReached(limits, "productVariants");
  const { PRODUCT_OVERVIEW_CREATE } = useExtensions(extensionMountPoints.PRODUCT_LIST);
  const extensionCreateButtonItems = getExtensionItemsForOverviewCreate(PRODUCT_OVERVIEW_CREATE);
  const [storedProductListViewType, setProductListViewType] = useLocalStorage<ProductListViewType>(
    "productListViewType",
    DEFAULT_PRODUCT_LIST_VIEW_TYPE,
  );
  const isDatagridView = storedProductListViewType === "datagrid";
  const isListView = storedProductListViewType === "list";

  return (
    <ListPageLayout>
      <TopNav
        withoutBorder
        isAlignToRight={false}
        title={intl.formatMessage(sectionNames.products)}
        subtitle={intl.formatMessage({
          id: "MeWtIH",
          defaultMessage: "Manage your store inventory and product catalog",
          description: "product list page subtitle",
        })}
      >
        <Box __flex={1} display="flex" justifyContent="flex-end" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            {hasLimits(limits, "productVariants") && (
              <Text size={2}>
                {intl.formatMessage(
                  {
                    id: "Kw0jHS",
                    defaultMessage: "{count}/{max} SKUs used",
                    description: "created products counter",
                  },
                  {
                    count: limits.currentUsage.productVariants,
                    max: limits.allowedUsage.productVariants,
                  },
                )}
              </Text>
            )}
            {onImport && (
              <Button variant="secondary" onClick={onImport} data-test-id="import-button">
                <Box display="flex" alignItems="center" gap={1}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8.75 14.25a.75.75 0 01-1.5 0V7.66L5.3 9.61a.75.75 0 11-1.06-1.06l3.5-3.5a.75.75 0 011.06 0l3.5 3.5a.75.75 0 11-1.06 1.06l-1.94-1.95v6.59z" />
                    <path d="M2.5 6.25a.75.75 0 00.75-.75v-1.5a.25.25 0 01.25-.25h9a.25.25 0 01.25.25v1.5a.75.75 0 001.5 0v-1.5A1.75 1.75 0 0012.5 2h-9A1.75 1.75 0 001.75 3.75v1.5a.75.75 0 00.75.75z" />
                  </svg>
                  <FormattedMessage id="9XUYQt" defaultMessage="Import" />
                </Box>
              </Button>
            )}
            <Button variant="secondary" onClick={onExport} data-test-id="export-button">
              <Box display="flex" alignItems="center" gap={1}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8.75 1.75a.75.75 0 00-1.5 0v6.59L5.3 6.39a.75.75 0 00-1.06 1.06l3.5 3.5a.75.75 0 001.06 0l3.5-3.5a.75.75 0 00-1.06-1.06l-1.94 1.95V1.75z" />
                  <path d="M2.5 9.75a.75.75 0 01.75.75v1.5a.25.25 0 00.25.25h9a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 0112.5 14h-9A1.75 1.75 0 011.75 12.25v-1.5a.75.75 0 01.75-.75z" />
                </svg>
                <FormattedMessage id="SVwJTM" defaultMessage="Export" />
              </Box>
            </Button>
            {extensionCreateButtonItems.length > 0 ? (
              <ButtonGroupWithDropdown
                onClick={onAdd}
                testId={"add-product"}
                options={extensionCreateButtonItems}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" />
                  </svg>
                  <FormattedMessage id="5lCY7S" defaultMessage="Add Product" />
                </Box>
              </ButtonGroupWithDropdown>
            ) : (
              <Button data-test-id="add-product" onClick={onAdd}>
                <Box display="flex" alignItems="center" gap={1}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" />
                  </svg>
                  <FormattedMessage id="5lCY7S" defaultMessage="Add Product" />
                </Box>
              </Button>
            )}
          </Box>
        </Box>
      </TopNav>
      {limitReached && (
        <LimitReachedAlert
          title={intl.formatMessage({
            id: "FwHWUm",
            defaultMessage: "SKU limit reached",
            description: "alert",
          })}
        >
          <FormattedMessage
            id="5Vwnu+"
            defaultMessage="You have reached your SKU limit, you will be no longer able to add SKUs to your store. If you would like to up your limit, contact your administration staff about raising your limits."
          />
        </LimitReachedAlert>
      )}
      <DashboardCard>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="stretch"
          justifyContent="space-between"
        >
          <ListFilters
            type="expression-filter"
            initialSearch={initialSearch}
            onSearchChange={onSearchChange}
            searchPlaceholder={intl.formatMessage({
              id: "kIvvax",
              defaultMessage: "Search Products...",
            })}
            actions={
              onBulkUpload && (
                <Button
                  variant="secondary"
                  onClick={() => setBulkUploadModalOpen(true)}
                  data-test-id="bulk-upload-button"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8.75 14.25a.75.75 0 01-1.5 0V7.66L5.3 9.61a.75.75 0 11-1.06-1.06l3.5-3.5a.75.75 0 011.06 0l3.5 3.5a.75.75 0 11-1.06 1.06l-1.94-1.95v6.59z" />
                      <path d="M2.5 6.25a.75.75 0 00.75-.75v-1.5a.25.25 0 01.25-.25h9a.25.25 0 01.25.25v1.5a.75.75 0 001.5 0v-1.5A1.75 1.75 0 0012.5 2h-9A1.75 1.75 0 001.75 3.75v1.5a.75.75 0 00.75.75z" />
                    </svg>
                    <FormattedMessage
                      id="p+QJfU"
                      defaultMessage="Upload Products in Bulk"
                      description="bulk upload products from csv file, button"
                    />
                  </Box>
                </Button>
              )
            }
          />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            paddingX={6}
            paddingBottom={4}
          >
            <Text size={3} color="default2">
              {listProps.products?.length || 0}{" "}
              {intl.formatMessage({
                id: "0aqtw1",
                defaultMessage: "products",
                description: "product count label",
              })}
            </Text>
            <ProductListViewSwitch
              defaultValue={storedProductListViewType}
              setProductListViewType={props => {
                setProductListViewType(props);
                clearRowSelection();
              }}
            />
          </Box>

          {/* Bulk Actions Bar */}
          {selectedProductIds.length > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              paddingX={6}
              paddingY={3}
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
              }}
              borderRadius={3}
              marginX={6}
              marginBottom={4}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Text size={3} fontWeight="medium">
                  {selectedProductIds.length}{" "}
                  {intl.formatMessage({
                    id: "5z21eg",
                    defaultMessage: "products selected",
                    description: "selected products count",
                  })}
                </Text>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={clearRowSelection}
                  data-test-id="clear-selection"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M3.72 3.72a.75.75 0 011.06 0L8 6.94l3.22-3.22a.75.75 0 111.06 1.06L9.06 8l3.22 3.22a.75.75 0 11-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 01-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 010-1.06z" />
                  </svg>
                </Button>
              </Box>
              <Box display="flex" gap={2}>
                <Button
                  variant="secondary"
                  onClick={onProductsDuplicate}
                  data-test-id="duplicate-button"
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4 2a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-1v1a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2h1V2zm1 3V2h8v8h-1V5a2 2 0 00-2-2H5zm5 9V5H3v9h7z" />
                    </svg>
                    <FormattedMessage defaultMessage="Duplicate" id="4fHiNl" />
                  </Box>
                </Button>
                <Button variant="secondary" onClick={onProductsDelete} data-test-id="delete-button">
                  <Box display="flex" alignItems="center" gap={1}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zm4.5 0V3h2.25a.75.75 0 010 1.5H2.75a.75.75 0 010-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675a.75.75 0 10-1.492.15l.66 6.6A1.75 1.75 0 005.405 15h5.19c.9 0 1.652-.681 1.741-1.576l.66-6.6a.75.75 0 00-1.492-.149l-.66 6.6a.25.25 0 01-.249.225h-5.19a.25.25 0 01-.249-.225l-.66-6.6z" />
                    </svg>
                    <FormattedMessage defaultMessage="Delete" id="K3r6DQ" />
                  </Box>
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        {isDatagridView ? (
          <ProductListDatagrid
            {...listProps}
            hasRowHover={true}
            filterDependency={filterDependency}
            activeAttributeSortId={activeAttributeSortId}
            defaultSettings={defaultSettings}
            availableColumnsAttributesOpts={availableColumnsAttributesOpts}
            loading={listProps.disabled}
            gridAttributesOpts={gridAttributesOpts}
            products={listProps.products}
            settings={settings}
            selectedChannelId={selectedChannelId}
            onUpdateListSettings={onUpdateListSettings}
            rowAnchor={productUrl}
            onRowClick={id => {
              navigate(productUrl(id), {
                state: getPrevLocationState(location),
              });
            }}
          />
        ) : isListView ? (
          <ProductListView
            {...listProps}
            settings={settings}
            loading={listProps.disabled}
            onUpdateListSettings={onUpdateListSettings}
            products={listProps.products}
            selectedChannelId={selectedChannelId}
            onItemClick={id => {
              navigate(productUrl(id));
            }}
          />
        ) : (
          <ProductListTiles
            {...listProps}
            settings={settings}
            loading={listProps.disabled}
            onUpdateListSettings={onUpdateListSettings}
            products={listProps.products}
            selectedChannelId={selectedChannelId}
            onTileClick={id => {
              navigate(productUrl(id));
            }}
          />
        )}
      </DashboardCard>

      {onBulkUpload && (
        <BulkProductUploadModal
          open={isBulkUploadModalOpen}
          onClose={() => setBulkUploadModalOpen(false)}
          onUpload={onBulkUpload}
        />
      )}
    </ListPageLayout>
  );
};
ProductListPage.displayName = "ProductListPage";
export default ProductListPage;
